import React, { useContext, useEffect, useMemo, useState } from 'react'
import { InformationalError, useShowError, useStorage } from '@aserto/console-common'

import { BaseConfiguration, Configurations } from '../../../common/types'
import { useConfiguration } from '../../api/internal'

const emptyConfig = {
  oidcClientId: '',
  oidcDomain: '',
  authenticationType: '',
  readOnly: false,
  configs: [],
}

type ConfigurationContextProps = {
  configurationName: string
  setConfigurationName: React.Dispatch<React.SetStateAction<string>>
  authorizerConfig: BaseConfiguration
  setAuthorizerConfig: React.Dispatch<React.SetStateAction<BaseConfiguration>>
  centralDirectoryConfig: BaseConfiguration
  setCentralDirectoryConfig: React.Dispatch<React.SetStateAction<BaseConfiguration>>
  config: Configurations
}

const ConfigurationContext = React.createContext<ConfigurationContextProps>({
  configurationName: '',
  setConfigurationName: () => {},
  authorizerConfig: {},
  setAuthorizerConfig: () => {},
  centralDirectoryConfig: {},
  setCentralDirectoryConfig: () => {},
  config: emptyConfig,
})

export const useConfigurations = () => {
  const config = useContext(ConfigurationContext)
  if (config.config === emptyConfig) {
    throw 'Config must be retrieved in the context of a <ConfigProvider>'
  }

  return config.config
}

export const useActiveConfigurationName = () => {
  return useContext(ConfigurationContext)
}

const ActiveConfigurationProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { data: config, error } = useConfiguration()

  const showError = useShowError()

  useEffect(() => {
    if (!!error) {
      showError(
        new InformationalError(
          'This may be a temporary error. Please try reloading this page. If the error persists, contact support@aserto.com.',
          'Looks like we are having issues with our services :('
        )
      )
    }
  }, [error, showError])

  const [configurationName, setConfigurationName] = useStorage<string>('activeConfiguration', '')
  const [authorizerConfig, setAuthorizerConfig] = useState<BaseConfiguration>({})
  const [centralDirectoryConfig, setCentralDirectoryConfig] = useState<BaseConfiguration>({})

  const value: ConfigurationContextProps = useMemo(
    () => ({
      configurationName: configurationName || '',
      setConfigurationName: setConfigurationName,
      authorizerConfig,
      setAuthorizerConfig,
      centralDirectoryConfig,
      setCentralDirectoryConfig,
      config: config!,
    }),
    [
      configurationName,
      setConfigurationName,
      authorizerConfig,
      setAuthorizerConfig,
      centralDirectoryConfig,
      setCentralDirectoryConfig,
      config,
    ]
  )
  return !config ? null : (
    <ConfigurationContext.Provider value={value}>{children}</ConfigurationContext.Provider>
  )
}

export default ActiveConfigurationProvider
